<template>
  <div>
    <div class="text-2xl text-black font-semibold">
      Cari Order & Label
    </div>
    <div class="d-flex justify-content-between mt-2">
      <div
        class="d-flex flex-grow-1 mr-3"
        style="max-width: 600px;"
      >
        <div class="search-box-wrapper d-flex align-items-start flex-grow-1">
          <div class="tags-input-container flex-grow-1 d-flex flex-column">
            <div
              v-if="tags.length > 0"
              class="tags-list d-flex flex-column align-items-start mb-1"
            >
              <div
                v-for="(tag, idx) in tags"
                :key="idx"
                class="search-tag d-flex align-items-center justify-content-between"
              >
                <span class="tag-text">{{ tag }}</span>
                <button
                  type="button"
                  class="btn-remove-tag ml-2"
                  @click="removeTag(idx)"
                >
                  &times;
                </button>
              </div>
            </div>
            <BFormInput
              v-if="tags.length < 100"
              v-model="newInputText"
              :placeholder="tags.length === 0 ? 'Cari No Order/Resi' : 'Tambahkan nomor... (Enter untuk memproses)'"
              class="search-input-clean"
              @keydown.enter.prevent="handleEnterKey"
              @paste="handlePaste"
              @blur="convertInputToTags"
            />
          </div>

          <div class="d-flex flex-column align-self-start ml-2">
            <BDropdown
              variant="outline-primary"
              toggle-class="h-[34px] flex items-center"
              :text="selectedSearchTypeLabel"
            >
              <BDropdownItem
                v-for="option in searchTypeOptions"
                :key="option.value"
                :active="searchType === option.value"
                @click="searchType = option.value"
              >
                {{ option.text }}
              </BDropdownItem>
            </BDropdown>
            <button
              v-if="tags.length >= 5"
              type="button"
              class="mt-1 w-full bg-transparent border-0 text-[#F95031] text-sm font-semibold cursor-pointer p-0 text-center transition-colors hover:text-[#c93d20] hover:underline focus:outline-none"
              @click="clearAllTags"
            >
              Hapus Semua
            </button>
          </div>
        </div>
        <BButton
          class="btn-search-orange ml-2"
          :disabled="(tags.length === 0 && !newInputText.trim()) || loadingSearch"
          @click="handleBulkSearch"
        >
          <BSpinner
            v-if="loadingSearch"
            small
          />
          <b-icon-search
            v-else
            font-scale="1.2"
          />
        </BButton>
      </div>

      <BButton
        class="btn-refresh-orange"
        :disabled="loadingSearch"
        @click="refreshSearch"
      >
        <b-icon-arrow-clockwise font-scale="1.2" />
      </BButton>
    </div>
    <div
      v-if="errorMessage"
      style="max-width: 540px;"
    >
      <div
        class="flex items-center bg-[#FFECE9] mt-2 p-[12px] rounded"
      >
        <b-icon-exclamation-circle
          font-scale="1.1"
          class="text-[#E84A2E] flex-shrink-0"
        />
        <span class="text-[#222222] ml-1">{{ errorMessage }}</span>
      </div>
    </div>

    <div
      v-if="notFoundNumbers.length > 0"
      class="mt-2"
      style="max-width: 540px;"
    >
      <div class="flex items-center justify-between bg-[#FEF0EE] border border-[#F4A398] rounded-2xl p-1">
        <div class="flex items-center gap-[6px]">
          <b-icon-exclamation-circle
            font-scale="1.1"
            class="text-[#E84A2E] flex-shrink-0"
          />
          <span class="text-[#E84A2E] text-sm font-semibold">{{ notFoundNumbers.length }} Nomor tidak ditemukan.</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="flex items-center gap-[6px] bg-white border border-[#D1D5DB] rounded-lg text-[#374151] text-xs font-medium cursor-pointer p-[6px] transition-all hover:border-[#F95031] hover:text-[#F95031] focus:outline-none shadow-sm"
            @click="copyNotFoundNumbers"
          >
            <b-icon-files
              v-if="!copiedNotFound"
              font-scale="0.9"
            />
            <b-icon-check2
              v-else
              font-scale="0.9"
              class="text-success"
            />
            {{ copiedNotFound ? 'Tersalin!' : 'Salin semua' }}
          </button>
          <button
            type="button"
            class="bg-transparent border-0 text-[#9CA3AF] text-xl leading-none cursor-pointer pl-1 pr-0 transition-colors hover:text-[#E84A2E] focus:outline-none"
            @click="notFoundNumbers = []"
          >
            &times;
          </button>
        </div>
      </div>
    </div>

    <BRow v-if="checkConnection === false">
      <BCol
        lg="12"
        class="flex items-center bg-[#FFECE9] mt-2 p-[12px] rounded"
      >
        <img
          src="https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/warning-2.svg"
          alt="Komerce"
        >
        <span class="text-[#222222] ml-1">Coba periksa koneksi kamu, dan coba lagi</span>
      </BCol>
    </BRow>

    <BCard
      v-if="showTable"
      class="mt-2"
    >
      <BOverlay
        :show="loadingSearch && tableItems.length === 0"
        spinner-variant="warning"
        variant="light"
        blur="0"
        opacity=".5"
        rounded="sm"
      >
        <div class="table-responsive">
          <BTable
            ref="resultTable"
            :fields="searchTableColumns"
            :items="tableItems"
            striped
            hover
            show-empty
            empty-text="Tidak ada data yang ditemukan"
            class="result-table mb-0"
          >
            <template #cell(order_date)="{ item }">
              <span class="text-black">{{ formatDateOnly(item.order_date) }}</span>
            </template>
            <template #cell(customer_name)="{ item }">
              <div class="cell-customer">
                <span class="customer-name">{{ item.customer_name || '-' }}</span>
                <div class="customer-shipping">
                  <img
                    v-if="item.shipment_image_path"
                    :src="item.shipment_image_path"
                    :alt="item.shipping"
                    class="shipping-logo"
                  >
                  <span class="shipping-type">{{ item.shipping_type || item.shipment_alias || item.shipping || '-' }}</span>
                </div>
              </div>
            </template>
            <template #cell(grandtotal)="{ item }">
              <div class="cell-grandtotal">
                <div class="grandtotal-row">
                  <span class="grandtotal-amount">{{ IDR(item.grandtotal) }}</span>
                  <b-icon-file-earmark-text class="invoice-icon ml-1" />
                </div>
                <span
                  v-if="item.payment_method"
                  class="payment-badge"
                  :class="item.payment_method === 'COD' ? 'payment-cod' : 'payment-transfer'"
                >
                  {{ item.payment_method }}
                </span>
              </div>
            </template>
            <template #cell(airway_bill)="{ item }">
              <div class="cell-resi">
                <span class="resi-number">{{ item.airway_bill || '-' }}</span>
                <button
                  v-if="item.airway_bill"
                  v-b-tooltip.hover
                  :title="copiedResi === item.airway_bill ? 'Tersalin!' : 'Salin nomor resi'"
                  type="button"
                  class="btn-copy-resi"
                  @click="copyResi(item.airway_bill)"
                >
                  <b-icon-check2
                    v-if="copiedResi === item.airway_bill"
                    font-scale="0.9"
                    class="text-success"
                  />
                  <b-icon-files
                    v-else
                    font-scale="0.9"
                  />
                </button>
              </div>
            </template>
            <template #cell(order_status)="{ item }">
              <BBadge
                pill
                :variant="getStatusVariant(item.order_status, item.is_retur)"
                class="status-badge"
              >
                {{ getStatusLabel(item.order_status) }}
                <span v-if="item.is_retur">
                  ({{ { 1: 'Dalam Perjalanan', 2: 'Diterima', 3: 'Luar Negeri' }[item.is_retur] }})
                </span>
              </BBadge>
            </template>
            <template #cell(detail)="{ item }">
              <a
                :href="getDetailUrl(item)"
                target="_blank"
                rel="noopener noreferrer"
                class="detail-link"
              >
                Lihat Detail
              </a>
            </template>
          </BTable>
        </div>

      </BOverlay>
    </BCard>
  </div>
</template>

<script>
import { komshipAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import moment from 'moment'
import {
  searchTableColumns,
  searchTypeOptions,
  STATUS_VARIANT_MAP,
  STATUS_LABEL_MAP,
} from './config'

export default {
  name: 'SearchOrderList',
  data() {
    return {
      IDR,
      searchTableColumns,
      searchTypeOptions,
      searchType: 'resi',
      newInputText: '',
      tags: [],
      loadingSearch: false,
      showTable: false,
      errorMessage: '',
      checkConnection: window.navigator.onLine,
      tableItems: [],
      limit: 100,
      lastSearchPayload: null,
      copiedResi: null,
      notFoundNumbers: [],
      copiedNotFound: false,
    }
  },

  computed: {
    selectedSearchTypeLabel() {
      const selected = this.searchTypeOptions.find(opt => opt.value === this.searchType)
      return selected ? selected.text : ''
    },
  },

  methods: {
    formatDateOnly(dateStr) {
      if (!dateStr) return '-'
      return moment(dateStr).format('DD MMMM YYYY')
    },
    getStatusVariant(status, isRetur) {
      if (isRetur === 3) return 'warning'
      return STATUS_VARIANT_MAP[status] || 'secondary'
    },
    getStatusLabel(status) {
      return STATUS_LABEL_MAP[status] || status || '-'
    },
    async copyResi(resi) {
      try {
        await navigator.clipboard.writeText(resi)
        this.copiedResi = resi
        setTimeout(() => { this.copiedResi = null }, 2000)
      } catch (err) {
        // eslint-disable-next-line no-empty
      }
    },
    getDetailUrl(item) {
      const qValue = item.order_no || item.airway_bill || item.order_id || item.id
      if (!qValue) return '#'
      const resolved = this.$router.resolve({
        name: 'search-order-detail',
        params: { id: qValue },
      })
      return resolved.href
    },
    handlePaste(e) {
      e.preventDefault()
      const clipboardData = e.clipboardData || window.clipboardData
      const pastedText = clipboardData.getData('Text')
      if (!pastedText) return

      const rawItems = pastedText
        .split(/[\r\n]+/)
        .map(l => l.trim())
        .filter(Boolean)

      const uniqueNewItems = [...new Set(rawItems)]
      const combined = [...new Set([...this.tags, ...uniqueNewItems])]

      if (combined.length > 100) {
        this.errorMessage = 'Maksimal data yang dapat dicari sekaligus adalah 100 data!'
        return
      }

      this.tags = combined
      this.newInputText = ''
      this.errorMessage = ''
    },

    handleEnterKey() {
      const text = this.newInputText.trim()
      if (!text) return

      const rawItems = text
        .split(/[\r\n]+/)
        .map(l => l.trim())
        .filter(Boolean)

      const uniqueNewItems = [...new Set(rawItems)]
      const combined = [...new Set([...this.tags, ...uniqueNewItems])]

      if (combined.length > 100) {
        this.errorMessage = 'Maksimal data yang dapat dicari sekaligus adalah 100 data!'
        return
      }

      this.tags = combined
      this.newInputText = ''
      this.errorMessage = ''
    },

    convertInputToTags() {
      this.handleEnterKey()
    },

    removeTag(idx) {
      this.tags.splice(idx, 1)
    },
    clearAllTags() {
      this.tags = []
      this.newInputText = ''
      this.errorMessage = ''
      this.notFoundNumbers = []
    },
    async copyNotFoundNumbers() {
      try {
        await navigator.clipboard.writeText(this.notFoundNumbers.join('\n'))
        this.copiedNotFound = true
        setTimeout(() => { this.copiedNotFound = false }, 2000)
      } catch (err) {
        // eslint-disable-next-line no-empty
      }
    },
    parseAndValidateInput() {
      if (this.tags.length === 0) {
        return { error: 'Masukkan setidaknya satu nomor untuk dicari.' }
      }
      if (this.tags.length > 100) {
        return { error: 'Maksimal data yang dapat dicari sekaligus adalah 100 data!' }
      }

      const ORDER_REGEX = /^KOM\d+$/
      const RESI_SAP = /^KOMSHIP\d+$/
      const RESI_NINJA = /^KOMRCKOM\d+$/

      const isOrderNumber = input => ORDER_REGEX.test(input)
      const isResiNumber = input => RESI_SAP.test(input) || RESI_NINJA.test(input)

      if (this.searchType === 'order_no') {
        const resiInputs = this.tags.filter(isResiNumber)
        if (resiInputs.length > 0) {
          const errorMsg = resiInputs.length === 1
            ? 'Ini adalah nomor resi, bukan nomor order.'
            : `Terdapat ${resiInputs.length} nomor resi yang terdeteksi. Ganti filter ke "Nomor Resi" atau hapus data yang salah.`
          return {
            error: errorMsg,
            invalidList: resiInputs,
          }
        }

        const invalidInputs = this.tags.filter(tag => !isOrderNumber(tag))
        if (invalidInputs.length > 0) {
          const errorMsg = invalidInputs.length === 1
            ? 'Format nomor order tidak valid (Harus diawali KOM dan angka).'
            : `Terdapat ${invalidInputs.length} format tidak valid. Nomor order harus diawali "KOM" diikuti angka (contoh: KOM123456).`
          return {
            error: errorMsg,
            invalidList: invalidInputs,
          }
        }
      }

      if (this.searchType === 'resi') {
        const orderInputs = this.tags.filter(isOrderNumber)
        if (orderInputs.length > 0) {
          const errorMsg = orderInputs.length === 1
            ? 'Ini adalah nomor order, bukan nomor resi.'
            : `Terdapat ${orderInputs.length} nomor order yang terdeteksi. Ganti filter ke "Nomor Order" atau hapus data yang salah.`
          return {
            error: errorMsg,
            invalidList: orderInputs,
          }
        }
      }

      return { data: this.tags }
    },
    async handleBulkSearch() {
      this.convertInputToTags()
      this.errorMessage = ''
      this.notFoundNumbers = []
      const { data, error } = this.parseAndValidateInput()
      if (error) {
        this.errorMessage = error
        return
      }

      this.tableItems = []
      this.showTable = false

      this.lastSearchPayload = {
        type: this.searchType,
        data: [...data],
      }

      await this.fetchBulkData()
    },
    async fetchBulkData() {
      if (!this.lastSearchPayload) return
      this.loadingSearch = true

      const { type, data } = this.lastSearchPayload
      const payload = {
        search_type: type,
        data,
        limit: this.limit,
        page: 1,
      }

      try {
        const res = await komshipAxiosIns.post('/v1/admin/order/bulk-search', payload)
        const resData = res.data
        const items = Array.isArray(resData.data) ? resData.data : []

        this.tableItems = items
        this.showTable = true

        const searchedNumbers = this.lastSearchPayload.data
        const foundNumbers = new Set()
        this.tableItems.forEach(item => {
          if (item.order_no) foundNumbers.add(item.order_no)
          if (item.airway_bill) foundNumbers.add(item.airway_bill)
        })
        this.notFoundNumbers = searchedNumbers.filter(num => !foundNumbers.has(num))
      } catch (err) {
        const message = err?.response?.data?.message || 'Terjadi kesalahan saat mencari data. Silakan coba lagi.'
        this.errorMessage = message
        this.showTable = this.tableItems.length > 0
      } finally {
        this.loadingSearch = false
      }
    },
    async refreshSearch() {
      if (!this.lastSearchPayload) return
      this.tableItems = []
      this.errorMessage = ''
      await this.fetchBulkData()
    },
  },
}
</script>

<style scoped lang="scss">
.search-box-wrapper {
  display: flex;
  align-items: start;
  border: 1px solid #D8D6DE;
  border-radius: 8px;
  background-color: #fff;
  padding: 8px 8px 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  flex: 1;
}

.tags-input-container {
  max-height: 195px;
  overflow-y: auto;
  padding-right: 4px;
}

.tags-input-container::-webkit-scrollbar {
  width: 6px;
}

.tags-input-container::-webkit-scrollbar-track {
  background: transparent;
}

.tags-input-container::-webkit-scrollbar-thumb {
  background: #C2C2C2;
  border-radius: 4px;
}

.tags-input-container::-webkit-scrollbar-thumb:hover {
  background: #A0A0A0;
}

.tags-list {
  width: 100%;
}

.search-tag {
  background-color: #FFECE9;
  color: #2D3748;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 6px;
}

.tag-text {
  word-break: break-all;
}

.btn-remove-tag {
  background: none;
  border: none;
  color: #626262;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.btn-remove-tag:hover {
  color: #E31A1A;
}

.btn-remove-tag:focus,
.btn-remove-tag:active {
  outline: none !important;
  box-shadow: none !important;
}

.search-box-wrapper:focus-within {
  border-color: #F95031;
  box-shadow: 0 0 0 0.2rem rgba(249, 80, 49, 0.25);
}

.search-input-clean {
  border: none !important;
  box-shadow: none !important;
}

.btn-search-orange {
  background-color: #F95031 !important;
  border-color: #F95031 !important;
  color: #fff !important;
  border-radius: 8px !important;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-search-orange:hover {
  background-color: #e04427 !important;
  border-color: #e04427 !important;
}

.btn-search-orange:disabled {
  background-color: #C2C2C2 !important;
  border-color: #C2C2C2 !important;
  cursor: not-allowed;
}

.btn-refresh-orange {
  border: 1px solid #F95031 !important;
  background-color: #fff !important;
  color: #F95031 !important;
  border-radius: 8px !important;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
}

.btn-refresh-orange:hover {
  background-color: #FFF2F0 !important;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #C2C2C2;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #A0A0A0;
  }
}

.result-table {
  ::v-deep th {
    background-color: #f8f9fa;
    white-space: nowrap;
  }
}

.detail-link {
  color: #08A0F7;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #0056b3;
  }
}

.cell-customer {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.customer-name {
  font-weight: 600;
  color: #222222;
  font-size: 13px;
}

.customer-shipping {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shipping-logo {
  height: 16px;
  width: auto;
  max-width: 48px;
  object-fit: contain;
}

.shipping-type {
  font-size: 12px;
  color: #626262;
}

.cell-grandtotal {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.grandtotal-row {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #222222;
}

.grandtotal-amount {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.invoice-icon {
  color: #626262;
  font-size: 14px;
}

.payment-badge {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.payment-cod {
  color: #F95031;
}

.payment-transfer {
  color: #28a745;
}

.cell-resi {
  display: flex;
  align-items: center;
  gap: 6px;
}

.resi-number {
  font-size: 13px;
  color: #222222;
  word-break: break-all;
}

.btn-copy-resi {
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #626262;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #F95031;
    background: #FFF2F0;
  }

  &:focus,
  &:active {
    outline: none !important;
    box-shadow: none !important;
  }
}

.status-badge {
  font-size: 12px;
  padding: 5px 12px;
  white-space: nowrap;
}
</style>
